"use client"

import { useBooking } from '@/contexts/BookingContext'
import { Button } from '@/components/ui/button'
import { CheckCircle, Calendar, Clock, User, MapPin } from 'lucide-react'
import { BookingStep } from '@/types/booking'
import { formatPrice, getBusinessCurrency } from '@/lib/utils'
import { formatScheduledDate, formatScheduledTime, parseScheduledWall } from '@/lib/salon-time'

export default function PurchaseSuccessful() {
  const {
    businessInfo,
    selectedSalon,
    selectedServices,
    selectedPackages,
    selectedStaff,
    appointmentDateTime,
    serviceClient,
    handleNextStep
  } = useBooking()
  
  const currency = getBusinessCurrency(businessInfo)
  const total = selectedServices.reduce((sum, s) => sum + s.price * (s.quantity || 1), 0) +
                selectedPackages.reduce((sum, p) => sum + p.totalPrice * p.quantity, 0)
  
  return (
    <div className="max-w-2xl mx-auto px-4 py-8 lg:px-20">
      <div className="bg-white rounded-xl border border-grey-100 p-6 text-center">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-grey-900 mb-2">Booking Confirmed!</h2>
        <p className="text-grey-600 mb-6">
          Your appointment has been successfully booked. We've sent a confirmation email to {serviceClient?.email}
        </p>
        
        <div className="bg-grey-50 rounded-lg p-6 text-left space-y-4 mb-6">
          <h3 className="font-semibold text-grey-900 mb-3">Booking Details</h3>
          
          {/* Client Info */}
          <div className="flex items-start gap-3">
            <User className="h-5 w-5 text-grey-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-grey-900">{serviceClient?.fullName}</p>
              <p className="text-sm text-grey-600">{serviceClient?.email}</p>
              <p className="text-sm text-grey-600">{serviceClient?.phone}</p>
            </div>
          </div>
          
          {/* Date & Time */}
          {appointmentDateTime && parseScheduledWall(appointmentDateTime) && (
            <div className="flex items-start gap-3">
              <Calendar className="h-5 w-5 text-grey-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-grey-900">
                  {formatScheduledDate(appointmentDateTime)}
                </p>
                <p className="text-sm text-grey-600">
                  {formatScheduledTime(appointmentDateTime)}
                </p>
              </div>
            </div>
          )}
          
          {/* Location */}
          <div className="flex items-start gap-3">
            <MapPin className="h-5 w-5 text-grey-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-grey-900">{businessInfo?.name}</p>
              <p className="text-sm text-grey-600">
                {selectedSalon?.address}, {selectedSalon?.city}
              </p>
            </div>
          </div>
          
          {/* Staff */}
          {selectedStaff && (
            <div className="flex items-start gap-3">
              <User className="h-5 w-5 text-grey-400 mt-0.5" />
              <div>
                <p className="text-sm text-grey-600">Staff</p>
                <p className="text-sm font-medium text-grey-900">
                  {selectedStaff.user?.firstName} {selectedStaff.user?.lastName}
                </p>
              </div>
            </div>
          )}
          
          {/* Services */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-semibold text-grey-900 mb-2">Services</h4>
            {selectedServices.map((service) => (
              <div key={service.id} className="flex justify-between text-sm mb-1">
                <span className="text-grey-600">
                  {service.name} x{service.quantity || 1}
                </span>
                <span className="font-medium text-grey-900">
                  {formatPrice(service.price * (service.quantity || 1), currency)}
                </span>
              </div>
            ))}
            {selectedPackages.map((pkg) => (
              <div key={pkg.id} className="flex justify-between text-sm mb-1">
                <span className="text-grey-600">
                  {pkg.name} x{pkg.quantity}
                </span>
                <span className="font-medium text-grey-900">
                  {formatPrice(pkg.totalPrice * pkg.quantity, currency)}
                </span>
              </div>
            ))}
            <div className="flex justify-between text-base font-semibold mt-3 pt-3 border-t">
              <span>Total</span>
              <span className="text-green-600">{formatPrice(total, currency)}</span>
            </div>
          </div>
        </div>
        
        <div className="flex gap-3">
          <Button
            variant="outline"
            className="flex-1"
            onClick={() => handleNextStep(BookingStep.INITIAL)}
          >
            Book Another
          </Button>
          <Button
            variant="primary"
            className="flex-1"
            onClick={() => window.print()}
          >
            Print Receipt
          </Button>
        </div>
      </div>
    </div>
  )
}
